import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material'
import { Provider } from 'react-redux'
import { store } from './store'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import LoginPage from './pages/LoginPage'
import { appTheme } from './theme/appTheme'

const HomePage = lazy(() => import('./pages/HomePage'))
const TeamsPage = lazy(() => import('./pages/TeamsPage'))
const CasesPage = lazy(() => import('./pages/CasesPage'))
const MemorialsPage = lazy(() => import('./pages/MemorialsPage'))
const EvidencePage = lazy(() => import('./pages/EvidencePage'))
const ScoringPage = lazy(() => import('./pages/ScoringPage'))
const ProfilePage = lazy(() => import('./pages/ProfilePage'))
const UsersPage = lazy(() => import('./pages/UsersPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const CourtroomPage = lazy(() => import('./pages/CourtroomPage'))
const ReportsPage = lazy(() => import('./pages/ReportsPage'))
const JudgeAssignmentsPage = lazy(() => import('./pages/JudgeAssignmentsPage'))
const BracketsPage = lazy(() => import('./pages/BracketsPage'))
const AchievementsPage = lazy(() => import('./pages/AchievementsPage'))
const VerdictsReviewPage = lazy(() => import('./pages/VerdictsReviewPage'))
const ExamInfoPage = lazy(() => import('./pages/ExamInfoPage'))
const PublicResultsPage = lazy(() => import('./pages/PublicResultsPage'))
const PublicLandingPage = lazy(() => import('./pages/PublicLandingPage'))
const PublicCourtroomWatchPage = lazy(() => import('./pages/PublicCourtroomWatchPage'))
const IntroVideoPage = lazy(() => import('./pages/IntroVideoPage'))
const SecretaryDeskPage = lazy(() => import('./pages/SecretaryDeskPage'))

/** Tải chunk route — hiển thị khi lazy import đang fetch */
function RouteFallback() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
      <CircularProgress color="primary" aria-label="Đang tải trang" />
    </Box>
  )
}

function withSuspense(node: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{node}</Suspense>
}
// DocumentsPage và RAGSearchPage đã được tích hợp vào CasesPage

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={appTheme}>
        <CssBaseline />
        <Box sx={{ 
          minHeight: '100dvh',
          width: '100%',
          margin: 0,
          padding: 0,
          bgcolor: 'background.default',
          overflowX: 'hidden',
          overflowY: 'auto',
        }}>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            {/* Public Guest routes — không cần đăng nhập */}
            <Route path="/" element={withSuspense(<PublicLandingPage />)} />
            <Route path="/public/results" element={withSuspense(<PublicResultsPage />)} />
            <Route path="/public/results/:caseId" element={withSuspense(<PublicResultsPage />)} />
            <Route path="/public/courtroom/:courtroomId" element={withSuspense(<PublicCourtroomWatchPage />)} />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<ProtectedRoute>{withSuspense(<HomePage />)}</ProtectedRoute>} />
              <Route path="/teams" element={<ProtectedRoute>{withSuspense(<TeamsPage />)}</ProtectedRoute>} />
              <Route path="/cases" element={<ProtectedRoute>{withSuspense(<CasesPage />)}</ProtectedRoute>} />
              <Route path="/memorials" element={<ProtectedRoute>{withSuspense(<MemorialsPage />)}</ProtectedRoute>} />
              <Route path="/evidence" element={<ProtectedRoute>{withSuspense(<EvidencePage />)}</ProtectedRoute>} />
              <Route path="/intro-video" element={<ProtectedRoute>{withSuspense(<IntroVideoPage />)}</ProtectedRoute>} />
              <Route
                path="/scoring"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'judge', 'secretary']}>
                    {withSuspense(<ScoringPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/judge-assignments"
                element={<ProtectedRoute>{withSuspense(<JudgeAssignmentsPage />)}</ProtectedRoute>}
              />
              <Route path="/courtroom" element={<ProtectedRoute>{withSuspense(<CourtroomPage />)}</ProtectedRoute>} />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'support', 'judge', 'admin']}>
                    {withSuspense(<ReportsPage />)}
                  </ProtectedRoute>
                }
              />
              <Route path="/brackets" element={<ProtectedRoute>{withSuspense(<BracketsPage />)}</ProtectedRoute>} />
              <Route
                path="/notifications"
                element={<ProtectedRoute>{withSuspense(<NotificationsPage />)}</ProtectedRoute>}
              />
              <Route path="/profile" element={<ProtectedRoute>{withSuspense(<ProfilePage />)}</ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute>{withSuspense(<UsersPage />)}</ProtectedRoute>} />
              <Route
                path="/achievements"
                element={<ProtectedRoute>{withSuspense(<AchievementsPage />)}</ProtectedRoute>}
              />
              <Route
                path="/verdicts-review"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'admin', 'support']}>
                    {withSuspense(<VerdictsReviewPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/secretary-desk"
                element={
                  <ProtectedRoute allowedRoles={['secretary', 'admin']}>
                    {withSuspense(<SecretaryDeskPage />)}
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cases/:caseId/exam"
                element={<ProtectedRoute>{withSuspense(<ExamInfoPage />)}</ProtectedRoute>}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        </Box>
      </ThemeProvider>
    </Provider>
  )
}

export default App
